import React from 'react';

import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';

import { ICarsDetailsState, ICarsReviewBookState, IInsuranceState, InsuranceSelection, insuranceActions } from '@store/slices';
import { ILoginState, IMenuState } from '@share/store/slices';
import {
  RootState,
  computeIsStripePaymentMethodsEnabled,
  isInsuranceBookingSelectionMissing,
  needsInsuranceQuoteResidencyStateDropdown,
  isEnableCreditLine,
} from '@share/utils';

import { CarsInfo } from '../cars-info';
import { CarsLocations } from '../cars-location';
import { CarsPolicies } from '../cars-policies';
import { CarsExtras } from '../cars-extras';

import { CarsPayment } from '../cars-payment';
import { CarsDisclaimers } from '../cars-disclaimers';
import { FormInstance } from 'antd/lib/form';
import { DriverInformation } from '../cars-driver-information';
import { CarsConfirmationEmail } from '../cars-confirmation-email';
import BillingAddressForm from '../billing-address-form/component';

import { CarsDetailsAnchors } from '../cars-details-anchors';
import { Insurance, ModalConfirmation } from '@components';

import './style.scss';
import { ButtonStripeComplete } from '../cars-details-wrapper';
import { isEmpty } from 'lodash';

interface IMapStateToProps {
  carsDetailsStore: ICarsDetailsState;
  carsReviewBookStore: ICarsReviewBookState;
  loginStore: ILoginState;
  navigationMenuStore: IMenuState;
  insuranceStore: IInsuranceState;
}

interface IMapDispatchToProps {
  setInsuranceSelection: (selection: InsuranceSelection | null) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  dealId: number;
  backHomeType: string;

  isCheckout: boolean;

  overviewRef: React.RefObject<HTMLDivElement>;
  paymentRef: React.RefObject<HTMLDivElement>;
  driverRef: React.RefObject<HTMLDivElement>;
  emailConfirmationRef: React.RefObject<HTMLDivElement>;
  reserveRef: React.RefObject<HTMLDivElement>;
  locationRef: React.RefObject<HTMLDivElement>;
  policiesRef: React.RefObject<HTMLDivElement>;
  extrasRef: React.RefObject<HTMLDivElement>;

  paymentFormRef: React.RefObject<FormInstance>;
  driverFormRef: React.RefObject<FormInstance>;
  emailFormRef: React.RefObject<FormInstance>;
  billingFormRef: React.RefObject<FormInstance>;
}

export interface IButtonsComplete {
  driverForm: boolean;
  paymentForm: boolean;
  emailForm: boolean;
  billingForm: boolean;
}

interface IMultiplePayments {
  amazonPay: boolean;
  applePay: boolean;
  googlePay: boolean;
  link: boolean;
  paypal: boolean;
}

interface IState {
  isButtonCompleteHidden: IButtonsComplete;
  multiplePayments: IMultiplePayments;
  paymentSelected: string;
  scrollToPaymentSelect: boolean;
  ifEmailChanging: string | undefined;
  expressWalletProbeComplete: boolean;
  hasExpressWalletMethods: boolean;
  forceFullStripeCheckoutPhase: 'details' | 'payment';
  pendingPaymentSwitch: string | null;
}

class CarsDetailsComponent extends React.Component<IProps, IState> {
  state: IState = {
    isButtonCompleteHidden: {
      driverForm: false,
      paymentForm: false,
      emailForm: false,
      billingForm: false
    },
    multiplePayments: {
      amazonPay: false,
      applePay: false,
      googlePay: false,
      link: false,
      paypal: false
    },
    paymentSelected: '',
    scrollToPaymentSelect: false,
    ifEmailChanging: undefined,
    expressWalletProbeComplete: false,
    hasExpressWalletMethods: false,
    forceFullStripeCheckoutPhase: 'details',
    pendingPaymentSwitch: null,
  };

  onExpressWalletAvailabilityResolved = (hasWallets: boolean) => {
    this.setState({
      expressWalletProbeComplete: true,
      hasExpressWalletMethods: hasWallets,
    });
  };

  insuranceContainerRef = React.createRef<HTMLDivElement>();

  componentDidUpdate(): void {
    const { account } = this.props.loginStore;
    const stripeRegionOk = computeIsStripePaymentMethodsEnabled(
      account,
      this.props.navigationMenuStore?.items?.siteId,
      this.props.navigationMenuStore?.items?.organizationId,
    );
    const suppressStripePaymentUI = stripeRegionOk && !!account?.forceStripeFull;
    if (!suppressStripePaymentUI && this.state.forceFullStripeCheckoutPhase !== 'details') {
      this.setState({ forceFullStripeCheckoutPhase: 'details' });
    }
  }

  render(): React.ReactNode {
    const {
      carsDetailsStore,
      carsReviewBookStore,
      loginStore,
      navigationMenuStore,
      insuranceStore,
      isCheckout,
      paymentFormRef,
      driverFormRef,
      emailFormRef,
      billingFormRef,
    } = this.props;
    const { car } = carsDetailsStore;
    const { bookingComplete, card, selectedCarsReviewClientCash } = carsReviewBookStore;
    const { selection, insurance, loading: insuranceLoading, error: insuranceError } = insuranceStore;

    if (bookingComplete) {
      return null;
    }

    const { account, userWallet } = loginStore;
    // Booking becomes non-refundable when wallet client cash is applied (mirrors hotels).
    const forceNonRefundable =
      !account?.walletSkipNonRefundable &&
      !account?.walletIsExternal &&
      !!account?.hasClientCash &&
      !!userWallet &&
      (selectedCarsReviewClientCash ?? 0) > 0;
    const skipPaymentCheckout = isEnableCreditLine(account);
    const stripeRegionOk =
      !skipPaymentCheckout &&
      computeIsStripePaymentMethodsEnabled(
        account,
        navigationMenuStore?.items?.siteId,
        navigationMenuStore?.items?.organizationId,
      );
    const suppressStripePaymentUI = stripeRegionOk && !!account?.forceStripeFull;
    const showExpressCheckoutFlow =
      !suppressStripePaymentUI &&
      stripeRegionOk &&
      this.state.expressWalletProbeComplete &&
      this.state.hasExpressWalletMethods;
    const paymentEffective = showExpressCheckoutFlow ? this.state.paymentSelected : 'card';

    const isCardPayment =
      !suppressStripePaymentUI &&
      !skipPaymentCheckout &&
      !isEmpty(paymentEffective) &&
      paymentEffective === 'card';
    const isOtherPayment =
      showExpressCheckoutFlow &&
      !isEmpty(this.state.paymentSelected) &&
      this.state.paymentSelected !== 'card';

    const showQuoteResidencyStateDropdown = needsInsuranceQuoteResidencyStateDropdown({
      stripeRegionOk,
      suppressStripePaymentUI,
      showExpressCheckoutFlow,
      cardHasSavedPaymentMethod: !!card?.id,
      isExpressWalletPaymentSelected:
        showExpressCheckoutFlow &&
        !isEmpty(this.state.paymentSelected) &&
        this.state.paymentSelected !== 'card',
    });

    const isForceFullStripePaymentStep =
      suppressStripePaymentUI && this.state.forceFullStripeCheckoutPhase === 'payment';
    // Insurance is never offered on cars.
    const showInsuranceGuestSection = false;
    const isInsuranceSelectionMissing = isInsuranceBookingSelectionMissing(
      showInsuranceGuestSection,
      account?.enableInsurance,
      insurance,
      insuranceLoading,
      insuranceError,
      navigationMenuStore?.items?.isRemoveInsurance,
      selection,
    );

    const shouldMountStripeExpressElements =
      !suppressStripePaymentUI &&
      stripeRegionOk &&
      (!this.state.expressWalletProbeComplete || this.state.hasExpressWalletMethods);
    const isEmailChanging = (ifEmailChanging: string | undefined) => this.setState({ifEmailChanging})

    const scrollToPaymentSelectDisabled = () => this.setState({ scrollToPaymentSelect: false });
    const scrollToPaymentSelectEnabled = () => this.setState({ scrollToPaymentSelect: true });

    const handleDriverFormValidation = (driverForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          driverForm,
        },
      }));
    };

    const handleEmailFormValidation = (emailForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          emailForm,
        },
      }));
    };

    const handlePaymentFormValidation = (paymentForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          paymentForm,
        },
      }));
    };

    const handleBillingFormValidation = (billingForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          billingForm,
        },
      }));
    };

    // Driver/email unmount on force-full payment step; details step already validated.
    const keysToValidate: Array<keyof IButtonsComplete> = isForceFullStripePaymentStep
      ? []
      : skipPaymentCheckout
        ? ['driverForm', 'emailForm', 'billingForm']
        : suppressStripePaymentUI
          ? ['driverForm', 'emailForm', 'billingForm']
          : isCardPayment
            ? ['driverForm', 'emailForm', 'paymentForm']
            : ['driverForm', 'emailForm', 'billingForm'];

    // Validate only the selected keys
    //VALIDATE THE SELECT PAYMENT FORM
    const formsStripeComplete = keysToValidate.every(
      (key) => this.state.isButtonCompleteHidden[key] === true,
    );
    const VALIDATE_STRIPE_BTNS =
      (skipPaymentCheckout || !isEmpty(paymentEffective)) &&
      formsStripeComplete &&
      !isInsuranceSelectionMissing;

    const setMultiplePayments = (payments: any) => {
      this.setState((prevState: IState) => ({
        ...prevState,
        multiplePayments: {
          ...payments,
        },
      }));
    };

    const setPaymentSelected = (payment: string) => {
      const isLeavingCard =
        this.state.paymentSelected === 'card' && !isEmpty(payment) && payment !== 'card';
      const insuranceOffered =
        showInsuranceGuestSection &&
        !!account?.enableInsurance &&
        !!insurance &&
        !navigationMenuStore?.items?.isRemoveInsurance;
      // Insurance only rides along with card payments. Warn before dropping it.
      if (isLeavingCard && insuranceOffered) {
        this.setState({ pendingPaymentSwitch: payment });
        return;
      }
      this.setState((prevState: IState) => ({
        ...prevState,
        paymentSelected: payment,
      }));
    };

    return (
      <>
        <CarsDetailsAnchors
          overviewRef={this.props.overviewRef}
          paymentRef={this.props.paymentRef}
          driverRef={this.props.driverRef}
          emailConfirmationRef={this.props.emailConfirmationRef}
          reserveRef={this.props.reserveRef}
          locationRef={this.props.locationRef}
          policiesRef={this.props.policiesRef}
          extrasRef={this.props.extrasRef}
          isCheckout={isCheckout}
          hideAnchorTabs={
            suppressStripePaymentUI &&
            this.state.forceFullStripeCheckoutPhase === 'payment'
          }
        />

        <div ref={this.props.overviewRef} />
        <CarsInfo car={car} forceNonRefundable={forceNonRefundable} />

        {isCheckout ? (
          <>
            {(!suppressStripePaymentUI ||
              this.state.forceFullStripeCheckoutPhase === 'details') && (
              <>
            <div ref={this.props.driverRef} />
            <DriverInformation
              formRef={this.props.driverFormRef}
              car={car}
              handleDriverFormValidation={handleDriverFormValidation}
              emailRef={this.props.emailFormRef}
              isEmailChanging={isEmailChanging}
            />
              </>
            )}

            {!skipPaymentCheckout ? (
              <>
                <div ref={this.props.paymentRef} />
                {/* Legacy card / wallet picker: hidden entirely when force-full Stripe (same as hotels PaymentMethod). */}
                {!suppressStripePaymentUI && (
                  <CarsPayment
                    formRef={this.props.paymentFormRef}
                    billingRef={this.props.billingFormRef}
                    car={car}
                    handleBillingFormValidation={handleBillingFormValidation}
                    multiplePayments={this.state.multiplePayments}
                    setPaymentSelected={setPaymentSelected}
                    paymentSelected={paymentEffective}
                    stripeExpressEnabled={showExpressCheckoutFlow}
                    handlePaymentFormValidation={handlePaymentFormValidation}
                    scrollToPaymentSelect={this.state.scrollToPaymentSelect}
                    scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
                  />
                )}
                {/* Force-full Stripe has no legacy picker, so collect the billing address here.
                    It feeds stripeInfo (carsReviewBookStore.stripe) -> customer.address on submit. */}
                {suppressStripePaymentUI &&
                  this.state.forceFullStripeCheckoutPhase === 'details' && (
                    <div className="hotel-info">
                      <div className="hotel-info__item border-top">
                        <div className="hotel-info__item-content">
                          <BillingAddressForm
                            formRef={this.props.billingFormRef}
                            loading={carsReviewBookStore.loadingBooking}
                            isRequired
                            handleBillingFormValidation={handleBillingFormValidation}
                            forceDisplay
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </>
            ) : (
              <div className="hotel-info">
                <div className="hotel-info__item border-top">
                  <div className="hotel-info__item-content">
                    <BillingAddressForm
                      formRef={this.props.billingFormRef}
                      loading={carsReviewBookStore.loadingBooking}
                      isRequired
                      handleBillingFormValidation={handleBillingFormValidation}
                      forceDisplay
                    />
                  </div>
                </div>
              </div>
            )}

            {(!suppressStripePaymentUI ||
              this.state.forceFullStripeCheckoutPhase === 'details') &&
              showInsuranceGuestSection ? (
              <div className="review-book__wrapper" ref={this.insuranceContainerRef}>
                <Insurance showQuoteResidencyStateDropdown={showQuoteResidencyStateDropdown} />
              </div>
            ) : null}

            {(!suppressStripePaymentUI ||
              this.state.forceFullStripeCheckoutPhase === 'details') && (
              <>
            <div ref={this.props.emailConfirmationRef} />
            <CarsConfirmationEmail
              formRef={this.props.emailFormRef}
              handleEmailFormValidation={handleEmailFormValidation}
              ifEmailChanging={this.state.ifEmailChanging}
            />
              </>
            )}

            <div ref={this.props.reserveRef} />
            <CarsDisclaimers
              driverFormRef={this.props.driverFormRef}
              paymentFormRef={this.props.paymentFormRef}
              billingRef={this.props.billingFormRef}
              emailFormRef={this.props.emailFormRef}
              isButtonCompleteHidden={this.state.isButtonCompleteHidden}
              setMultiplePayments={setMultiplePayments}
              paymentSelected={paymentEffective}
              expressCheckoutPaymentSelected={this.state.paymentSelected}
              validateStripeBtns={VALIDATE_STRIPE_BTNS}
              scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
              scrollToPaymentSelectEnabled={scrollToPaymentSelectEnabled}
              shouldMountStripeExpressElements={shouldMountStripeExpressElements}
              expressWalletProbeComplete={this.state.expressWalletProbeComplete}
              onExpressWalletAvailabilityResolved={this.onExpressWalletAvailabilityResolved}
              suppressStripePaymentUI={suppressStripePaymentUI}
              forceFullStripeCheckoutPhase={this.state.forceFullStripeCheckoutPhase}
              onForceFullStripeCheckoutNext={() =>
                this.setState({ forceFullStripeCheckoutPhase: 'payment' })
              }
              onForceFullStripeCheckoutBack={() =>
                this.setState({ forceFullStripeCheckoutPhase: 'details' })
              }
              insuranceContainerRef={this.insuranceContainerRef}
              insuranceRequiredMessageDisplay={
                !!(
                  isInsuranceSelectionMissing &&
                  (!suppressStripePaymentUI ||
                    this.state.forceFullStripeCheckoutPhase === 'details')
                )
              }
            />

            <ModalConfirmation
              message="insurance.payment.switch.message"
              title="insurance.payment.switch.title"
              yesMessage="insurance.payment.switch.continue"
              noMessage="insurance.payment.switch.keep"
              visible={!!this.state.pendingPaymentSwitch}
              onCancel={() => this.setState({ pendingPaymentSwitch: null })}
              onConfirm={() => {
                const nextPayment = this.state.pendingPaymentSwitch as string;
                this.props.setInsuranceSelection(InsuranceSelection.NO);
                this.setState({ pendingPaymentSwitch: null, paymentSelected: nextPayment });
              }}
            />
          </>
        ) : (
          <>
            <div ref={this.props.locationRef} />
            <CarsLocations car={car} />

            <div ref={this.props.policiesRef} />
            <CarsPolicies car={car} />

            <CarsExtras display={!isCheckout} extrasRef={this.props.extrasRef} />
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsDetailsStore: state.carsDetailsStore,
    carsReviewBookStore: state.carsReviewBookStore,
    loginStore: state.loginStore,
    navigationMenuStore: state.navigationMenuStore,
    insuranceStore: state.insuranceStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setInsuranceSelection: insuranceActions.setSelection,
};

export const CarsDetails = connect(mapStateToProps, mapDispatchToProps)(withRouter(CarsDetailsComponent));

